/**
 * Admin page: Battery Presets
 * Save MIS-3012 battery storage technical info per manufacturer + model.
 * The Contract Pack wizard auto-fills the Battery Storage Technical
 * Information table whenever the customer's battery matches an entry here.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, Pencil, Loader2, Battery, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { CsvImportDialog } from '../components/CsvImportDialog';

const SPEC_FIELDS = [
  { key: 'system_type', label: 'EESS type', type: 'select', options: ['Off-The Shelf', 'Composite'] },
  { key: 'dimensions', label: 'Dimensions (W×D×H mm)', type: 'text', placeholder: '500x232x915' },
  { key: 'weight', label: 'Weight', type: 'text', placeholder: '128.8kg' },
  { key: 'coupling', label: 'AC or DC coupled?', type: 'select', options: ['DC Coupled', 'AC Coupled'] },
  { key: 'inverter_status', label: 'In-situ inverter', type: 'select', options: ['New Inverter', 'Existing Inverter'] },
  { key: 'max_power_kw', label: 'Max power output (kW)', type: 'text', placeholder: '8.2' },
  { key: 'max_current_a', label: 'Max continuous current (A)', type: 'text', placeholder: '160' },
  { key: 'usable_capacity_kwh', label: 'Useable storage capacity (kWh)', type: 'text', placeholder: '12.9' },
  { key: 'round_trip_efficiency', label: 'Round-trip efficiency (%)', type: 'text', placeholder: '95' },
  { key: 'grid_charge_loss_kwh', label: 'Energy loss charged from grid (kWh)', type: 'text', placeholder: '0.65' },
  { key: 'replacement_module_cost', label: 'Replacement module cost (£)', type: 'text', placeholder: '2100' },
  { key: 'island_mode', label: 'Island mode? Max load (kW or N/A)', type: 'text', placeholder: 'N/A' },
  { key: 'eess_class', label: 'EESS Classification (MIS 3012)', type: 'select', options: ['1', '2', '3', '4'] },
  { key: 'battery_type', label: 'Battery chemistry / type', type: 'text', placeholder: 'LiFePO4' },
  { key: 'soh_method', label: 'State-of-health indicator method', type: 'text', placeholder: 'On-Screen Menu' },
  { key: 'proposed_location', label: 'Proposed location', type: 'text', placeholder: 'Garage' },
];

const empty = {
  manufacturer: '', model: '',
  system_type: 'Off-The Shelf', coupling: 'DC Coupled', inverter_status: 'New Inverter',
  eess_class: '1', island_mode: 'N/A',
};

export const BatteryPresetsContent = ({ embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/battery-presets');
      setRows(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!editing.manufacturer?.trim() || !editing.model?.trim()) {
      toast.error('Manufacturer and model are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.created_at; delete payload.created_by;
      delete payload.updated_at; delete payload.updated_by;
      if (editing.id) {
        delete payload.id;
        await api.put(`/battery-presets/${editing.id}`, payload);
      } else {
        await api.post('/battery-presets', payload);
      }
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (id, label) => {
    if (!window.confirm(`Delete battery preset "${label}"?`)) return;
    try {
      await api.delete(`/battery-presets/${id}`);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="battery-presets-page">
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Battery className="w-6 h-6 text-sky-600" />
              Battery Presets
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              MIS-3012 battery storage technical info. Auto-populates the Quotation table when a customer's battery matches.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn">
              <Upload className="w-4 h-4 mr-1" /> Import CSV
            </Button>
            <Button onClick={() => setEditing({ ...empty })} className="bg-sky-600 hover:bg-sky-700" data-testid="add-battery-preset-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Battery
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end gap-2 mb-3">
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="open-import-csv-btn" size="sm">
            <Upload className="w-4 h-4 mr-1" /> Import CSV
          </Button>
          <Button onClick={() => setEditing({ ...empty })} className="bg-sky-600 hover:bg-sky-700" data-testid="add-battery-preset-btn" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Add Battery
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Saved batteries ({rows.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-400 italic py-6 text-center">No battery presets yet. Add common batteries so the wizard auto-fills them.</p>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 p-3 rounded border border-slate-200 hover:bg-slate-50" data-testid={`battery-preset-row-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] border-sky-200 text-sky-700">{r.manufacturer}</Badge>
                    <span className="font-medium text-sm text-slate-800">{r.model}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {r.usable_capacity_kwh && <>Useable: {r.usable_capacity_kwh}kWh · </>}
                    {r.max_power_kw && <>Max: {r.max_power_kw}kW · </>}
                    {r.coupling}{r.battery_type ? ` · ${r.battery_type}` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r })} className="h-8 text-xs" data-testid={`edit-battery-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r.id, `${r.manufacturer} ${r.model}`)} className="h-8 text-xs text-rose-600 hover:bg-rose-50" data-testid={`delete-battery-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="battery-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Battery Preset' : 'Add Battery Preset'}</DialogTitle>
            <DialogDescription>
              All 14 fields below populate the Battery Storage Technical Information table on the Quotation page.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Manufacturer</Label>
                  <Input value={editing.manufacturer} onChange={(e) => setEditing({ ...editing, manufacturer: e.target.value })} placeholder="Pylontech" data-testid="battery-mfr-input" />
                </div>
                <div>
                  <Label className="text-xs">Model</Label>
                  <Input value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} placeholder="US5000 4.8kWh" data-testid="battery-model-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                {SPEC_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    {f.type === 'select' ? (
                      <Select value={editing[f.key] || ''} onValueChange={(v) => setEditing({ ...editing, [f.key]: v })}>
                        <SelectTrigger className="h-9 text-sm" data-testid={`battery-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        className="h-9 text-sm"
                        value={editing[f.key] || ''}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                        placeholder={f.placeholder || ''}
                        data-testid={`battery-field-${f.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="battery-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing?.id ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <CsvImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import Battery Presets from CSV"
        description="Bulk load battery models from a spreadsheet. Rows are upserted by manufacturer + model — re-uploading an updated CSV will refresh existing entries."
        templateUrl="/battery-presets/template.csv"
        importUrl="/battery-presets/import-csv"
        templateFilename="battery-presets-template.csv"
        onSuccess={reload}
        colorClass="bg-sky-600 hover:bg-sky-700"
        iconColor="text-sky-600"
      />
    </div>
  );
};

export default function BatteryPresets() {
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <BatteryPresetsContent />
    </div>
  );
}
